// Live score preview for the review form.
//
// This DUPLICATES src/engine/scoring.js on purpose — the reviewer needs the number to move
// as they click, and a round-trip per click is not that. It is a PREVIEW: the value posted
// to the server is ignored, the server recomputes from the same criteria table, and the
// response carries the authoritative figure which the form then shows. If the two ever
// disagree, the server is right by definition.
//
// The weights come from /api/meta (the DB table), not from a constant here, so a weight
// change reaches this preview without a redeploy.

import type { Criterion } from './types';

export interface LiveScore {
  /** What the call scores once the critical override is applied. */
  final: number | null;
  /** The weighted average before the override — shown as "kriter ortalaması". */
  weighted: number | null;
  hasCritical: boolean;
  /** true once every criterion has a score, i.e. the form can be submitted. */
  complete: boolean;
}

export function liveScore(
  scores: Record<string, number | null>,
  criticals: string[],
  criteria: Criterion[],
): LiveScore {
  const hasCritical = criticals.length > 0;
  if (!criteria.length) return { final: null, weighted: null, hasCritical, complete: false };

  let acc = 0;
  let weightUsed = 0;
  let complete = true;
  for (const c of criteria) {
    const v = scores[c.key];
    if (v === null || v === undefined) { complete = false; continue; }
    acc += v * c.weight;
    weightUsed += c.weight;
  }

  // Partway through the form, average over the criteria scored SO FAR rather than treating
  // the unscored ones as 0 — a form that starts at 0,0 and climbs reads as a low score.
  const weighted = weightUsed > 0
    ? Math.round((acc / weightUsed + Number.EPSILON) * 10) / 10
    : null;

  return { final: hasCritical ? 0 : weighted, weighted, hasCritical, complete };
}

/**
 * The form's mirror of engine/scoring.js `audioRequired`. A recording is now required on
 * EVERY call (owner 2026-09-07), so it no longer depends on the score — this just echoes the
 * server's policy flag from /api/meta (`audio.required`), keeping the rule in exactly one
 * place exactly as when it was score-dependent.
 */
export function audioRequired(required: boolean): boolean {
  return required;
}

/**
 * The form's copy of engine/scoring.js `noteRequired`: anything short of a perfect 10
 * needs the QMS note. Threshold from /api/meta (`notes.requiredBelow`), same as the audio
 * rule — and the same incomplete-form answer: not knowable yet, so not demanded yet.
 */
export function noteRequired(live: LiveScore, requiredBelow: number): boolean {
  if (live.hasCritical) return true;
  if (!live.complete || live.final === null) return false;
  return live.final < requiredBelow;
}
